import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination as AntPagination, Select } from 'antd-v5';
import { PaginationWrapper } from 'components/Pagination/Pagination.styled';
import { Flex } from 'UI';
import { Text } from 'UI/Text';

interface PaginationProps {
  onPageChange: (page: number, pageSize: number) => void;
  total: number;
  pageSizeOptions?: number[];
  pageSize?: number;
  currentPage?: number;
  showQuickJumper?: boolean;
  showPageSizeSelector?: boolean;
}

export const Pagination = ({
  onPageChange,
  total = 0,
  pageSizeOptions = [10, 20, 50, 100],
  pageSize = 50,
  currentPage = 1,
  showQuickJumper = true,
  showPageSizeSelector = true,
}: PaginationProps) => {
  const { t } = useTranslation();

  const handlePageSizeChange = (newPageSize: number) => {
    onPageChange(1, newPageSize);
  };

  const handlePageChange = (page: number) => {
    onPageChange(page, pageSize);
  };

  // Calculate effective page to handle cases where currentPage exceeds max pages
  const maxPage = total > 0 ? Math.ceil(total / pageSize) : 1;
  const effectivePage = Math.min(currentPage, maxPage);

  const rangeStart = total === 0 ? 0 : (effectivePage - 1) * pageSize + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(effectivePage * pageSize, total);

  return (
    <PaginationWrapper align={'center'} justify={'space-between'}>
      <Flex gap={12} align={'center'}>
        {showPageSizeSelector && (
          <>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              options={pageSizeOptions.map((size) => ({ value: size, label: `${size}` }))}
            />
            <Text type="base" color="grey35" disableGutter>
              {t('pagination.items_per_page', 'Items per page')}
            </Text>
            <Text type="base" color="grey35" disableGutter>
              |
            </Text>
          </>
        )}
        <Text type="base" color="grey35" disableGutter>
          {t('pagination.items_range', '{{rangeStart}}-{{rangeEnd}} of {{total}} items', {
            rangeStart,
            rangeEnd,
            total,
          })}
        </Text>
      </Flex>
      <AntPagination
        current={currentPage}
        pageSize={pageSize}
        total={total}
        onChange={handlePageChange}
        showSizeChanger={false}
        showQuickJumper={
          showQuickJumper
            ? {
                goButton: (
                  <Text type="base" bold className="pagination-jumper-text" disableGutter>
                    {t('pagination.go', 'Go')}
                  </Text>
                ),
              }
            : false
        }
        simple={false}
        locale={{
          jump_to: t('pagination.page', 'Page'),
          page: '',
        }}
      />
    </PaginationWrapper>
  );
};
